import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import {
  AfterViewInit,
  booleanAttribute,
  Component,
  computed,
  ElementRef,
  inject,
  OnChanges,
  OnInit,
  QueryList,
  SimpleChanges,
  ViewChildren,
  ChangeDetectionStrategy,
  input,
  output,
  DestroyRef,
} from '@angular/core'
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms'

import { combineLatest, Observable, of } from 'rxjs'
import { debounceTime, map, startWith, switchMap, take } from 'rxjs/operators'

import { Select as McuiSelect, FileAvatar, Switch, FirstCharacterPipe } from '@performance/ui'
import { TranslateService, TranslateDirective, TranslatePipe } from '@ngx-translate/core'

import { environment } from '@env'
import { cloneObject, isIncluded, uniqueId } from '@core/utils/main'
import { UsersBOService } from '@features/bo-users/usersBO.service'
import { UserProfileType } from '@features/user-customers/users/users.enums'
import { UserCustomerViewService } from '@features/user-customers/user-customer-view.service'

import { FORM_INPUTS, PASSWORD_EXPIRATION_OPTIONS, VALIDATORS } from './general-form.constants'
import { FormInputSetting, InputSetting } from './general-form.models'
import { InputType, TemplateType } from './general-form.enums'
import { User } from '@features/user-customers/users/users.models'
import { UserBO } from '@features/bo-users/usersBO.models'
import { UserProfile } from '@core/authentication/auth/auth.models'
import { ProfileStore } from '@core/store/profile/profile.store'
import { SelectButton } from '@shared/modules/primeng-clone/selectbutton'
import { Heading } from '../heading/heading'
import { NgTemplateOutlet, AsyncPipe } from '@angular/common'
import { Button } from '../button/button'
import { InputText } from '@shared/modules/primeng-clone/inputtext'
import { Dropdown } from '../dropdown/dropdown'
import { Calendar } from '../calendar/calendar'
import { ApplicationAccessTable } from '@features/user-customers/application-access-table/application-access-table'
import { PrimeTemplate } from '@shared/modules/primeng-clone/api'
import { Tooltip } from '@shared/modules/primeng-clone/tooltip'

@Component({
  selector: 'bo-general-form',
  templateUrl: './general-form.html',
  styleUrls: ['./general-form.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    FileAvatar,
    Heading,
    NgTemplateOutlet,
    Button,
    TranslateDirective,
    InputText,
    Dropdown,
    McuiSelect,
    Switch,
    Calendar,
    ApplicationAccessTable,
    SelectButton,
    PrimeTemplate,
    Tooltip,
    AsyncPipe,
    TranslatePipe,
    FirstCharacterPipe,
  ],
})
export class GeneralForm implements OnInit, AfterViewInit, OnChanges {
  readonly #destroyRef = inject(DestroyRef)
  readonly #fb = inject(UntypedFormBuilder)
  readonly #userBOService = inject(UsersBOService)
  readonly #translate = inject(TranslateService)
  readonly #userCustomerViewService = inject(UserCustomerViewService)

  readonly #profileStore = inject(ProfileStore)

  private _inputs = {
    disabled: [],
    multiSwitch: [],
  }
  private _selectButtons = {}

  readonly userData = input<UserProfile | UserBO | User>(undefined)
  readonly isEdit = input(false)
  readonly isProfileView = input(false, { transform: booleanAttribute })
  readonly type = input(TemplateType.UserProfile)

  readonly onChange = output<UserProfile | User>()
  readonly revokePassword = output<string[]>()
  readonly resendWelcomeEmail = output<string[]>()

  @ViewChildren('selectButtons') selectButtonComponents: QueryList<SelectButton>
  @ViewChildren('selectButtons', { read: ElementRef }) selectButtonElemRefs: QueryList<ElementRef>
  @ViewChildren(McuiSelect) dropdowns: QueryList<McuiSelect<any>>

  VALIDATORS = VALIDATORS
  InputType = InputType
  TemplateType = TemplateType
  isIncluded = isIncluded

  userForm: UntypedFormGroup
  formInputs: FormInputSetting
  uniqueIdPrefix = uniqueId()
  language$: Observable<string>

  readonly isAdmin = this.#profileStore.isAdmin
  readonly defaultAvatarColor = '#a1a1a1'

  // `toObservable` requires an injection context (NG0203 otherwise). These
  // observables are created here — field initializers run within one — because
  // `resolveInputOptions()` is invoked from ngOnInit/ngOnChanges, which are not.
  readonly #isAdmin$ = toObservable(this.isAdmin)
  readonly #languageOptions$ = toObservable(
    computed(() => this.#profileStore.allAvailableLanguages(this.isProfileView())),
  )

  constructor() {
    this.language$ = this.#translate.onLangChange.pipe(
      map((e) => e.lang),
      startWith(this.#translate.currentLang()),
    )
  }

  private _createFormInputs() {
    this.formInputs = Object.entries(cloneObject(FORM_INPUTS)).reduce(
      (obj, [key, inputSetting]) => ({
        ...obj,
        ...((inputSetting as InputSetting).templates.includes(this.type())
          ? { [key]: inputSetting }
          : {}),
      }),
      {},
    )
    // this._initIsAdminTrigger()
  }

  private _buildForm() {
    const disableInputKeys = []
    const multiSwitchInputKeys = []
    const controlsConfig = Object.entries(this.formInputs).reduce((obj, [key, inputInfo]) => {
      if (inputInfo.disabled) {
        disableInputKeys.push(key)
      }
      if (inputInfo.type === InputType.SwitchMultiple) {
        multiSwitchInputKeys.push(key)
      }
      return {
        ...obj,
        [key]: [
          { value: inputInfo.value || '', disabled: !!inputInfo.disabled },
          inputInfo.validators || [],
        ],
      }
    }, {})
    this.userForm = this.#fb.group(controlsConfig)
    this._inputs.disabled = disableInputKeys.map((inputKey) => this.userForm.get(inputKey))
    this._inputs.multiSwitch = multiSwitchInputKeys
  }

  private _updateFormFields(params?) {
    const userData = this.userData()
    if (userData && JSON.stringify(userData) !== '{}') {
      if (this._inputs.multiSwitch.length > 0) {
        this._inputs.multiSwitch
          .filter((inputKey) => !!this._selectButtons[inputKey])
          .forEach((inputKey) => {
            this._selectButtons[inputKey]
              .options()
              .forEach(
                (option) => (option.checked = this.userData()[inputKey].includes(option.value)),
              )
          })
      }
    }

    this.userForm.reset(userData || {}, params)
  }

  private _checkFormEnableness() {
    this.userForm[this.isEdit() ? 'enable' : 'disable']({ emitEvent: false })
    this._inputs.disabled.forEach((input) => input.disable({ emitEvent: false }))
    if (this.isEdit()) {
      Object.keys(this.userForm.controls).forEach((key) =>
        this.userForm.controls[key].markAsTouched(),
      )
    }
  }

  private _formSubscription() {
    this.userForm.valueChanges
      .pipe(debounceTime(environment.DEBOUNCE_TIME.LONG), takeUntilDestroyed(this.#destroyRef))
      .subscribe((userFormValue) => {
        if (this.userForm.dirty && this.userForm.valid) {
          const payload = { ...userFormValue }
          if (payload.profileIds && Array.isArray(payload.profileIds)) {
            payload.profileIds = payload.profileIds.map((profile) => {
              if (typeof profile === 'object' && profile !== null) {
                return profile
              }
              const originalProfile = (this.userData() as User)?.profiles?.find(
                (p: any) => p.id === profile,
              )
              if (originalProfile) {
                return {
                  id: originalProfile.id,
                  ...(originalProfile.license ? { license: originalProfile.license } : {}),
                }
              }
              return { id: profile }
            })
          }
          this.onChange.emit(payload)
        } else {
          this.onChange.emit(null)
        }
      })
  }

  ngOnInit() {
    this._createFormInputs()
    this._buildForm()
    this.resolveInputOptions()
    this._updateFormFields()
    this._checkFormEnableness()
    this._formSubscription()
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['userData']) {
      const user = changes['userData'].currentValue

      if (!changes['userData'].isFirstChange()) {
        this._updateFormFields({ emitEvent: false })
      }
      if (user) {
        this.resolveInputOptions()
      }
    }
    if (changes['isEdit'] && !changes['isEdit'].isFirstChange()) {
      this._checkFormEnableness()
    }
  }

  ngAfterViewInit() {
    if (this.selectButtonComponents) {
      this.selectButtonElemRefs.forEach((item, index) => {
        this._selectButtons[item.nativeElement.id] = this.selectButtonComponents.find(
          (a, i) => i === index,
        )
      })
    }
  }

  shouldDisableOption(value, isAdmin) {
    return value === UserProfileType.BackOfficeUserProfile && !isAdmin
  }

  resetForm() {
    this._updateFormFields({ emitEvent: false })
  }

  forceDetectChanges() {
    // Confirmed still load-bearing under zoneless (verified by temporarily emptying this body):
    // @performance/ui's SelectComponent (OnPush) doesn't repaint its [disabled] state on its own
    // when the form's mode flips Read->Edit, even though the triggering click is fully
    // synchronous and would normally auto-schedule a check. Each dropdown needs its own
    // explicit detectChanges() call — a parent-level check isn't enough to reach it.
    this.dropdowns.forEach((d) => d.detectChanges())
  }

  onRevokePassword() {
    this.revokePassword.emit([this.userData().id])
  }

  onResendWelcomeEmail() {
    this.resendWelcomeEmail.emit([this.userData().id])
  }

  onAvatarChange(avatar: any) {
    if (avatar.result) {
      this.userForm.get('photoPath').patchValue(avatar.result)
    }
  }

  resolveInputOptions() {
    if (!this.formInputs) {
      return
    }
    if (this.formInputs['customerId']) {
      this.formInputs['customerId'].options = this.#userCustomerViewService.getCustomerOptions$()
    }
    if (this.formInputs['profileIds']) {
      const userProfilesMap = new Map(
        (this.userData() as User)?.profiles?.map((profile: any) => [profile.id, profile]) || [],
      )
      const expirationDate$ = this.userForm
        .get('activePeriodEnd')
        .valueChanges.pipe(startWith(this.userForm.get('activePeriodEnd').value))
      this.formInputs['profileIds'].options = this.#userCustomerViewService
        .getUserProfileTypeOptions$()
        .pipe(
          switchMap((options) =>
            combineLatest([of(options), this.#isAdmin$.pipe(take(1)), expirationDate$]),
          ),
          map(([options, isAdmin, currentExpirationDate]) =>
            options.map((option) => {
              const matchingProfile = userProfilesMap.get(option.value)
              return {
                ...option,
                disabled: this.shouldDisableOption(option.value, isAdmin),
                license: matchingProfile?.license || null,
                expirationDate: currentExpirationDate,
              }
            }),
          ),
        )
    }
    if (this.formInputs['roleIds']) {
      this.formInputs['roleIds'].options = this.#userBOService.getRoleOptions$().pipe(
        map((options: any[]) =>
          options.map((option) => ({
            ...option,
            checked: (this.userData() as UserBO)?.roleIds?.includes(option.value) || false,
          })),
        ),
      )
    }
    if (this.formInputs['language']) {
      // Was `store.select(selectAllAvailableLanguages(this.isProfileView()))`:
      // the old selector factory is now a store method, wrapped in a
      // `computed` and converted to an Observable in the `#languageOptions$`
      // field initializer (in injection context) to keep it reactive and
      // exposed as an Observable (the form input setting expects one).
      this.formInputs['language'].options = this.#languageOptions$
    }
    if (this.formInputs['positionId']) {
      this.formInputs['positionId'].options = this.#userCustomerViewService.getPositionOptions$()
    }
    if (this.formInputs['categoryId']) {
      this.formInputs['categoryId'].options =
        this.#userCustomerViewService.getCategoryGroupOptions$()
    }
    if (this.formInputs['passwordExpiration']) {
      this.formInputs['passwordExpiration'].options = of(PASSWORD_EXPIRATION_OPTIONS)
    }
  }
}
